/**
 * Shared request utilities for theme AJAX flows.
 * Normalizes timeout, retry, and error objects.
 */
(function() {
  'use strict';

  function createError(kind, message, details) {
    const error = new Error(message);
    error.kind = kind;
    if (details && typeof details === 'object') {
      Object.assign(error, details);
    }
    return error;
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function isRetryable(error) {
    if (!error) return false;
    if (error.kind === 'timeout' || error.kind === 'network') return true;
    if (error.kind === 'http' && typeof error.status === 'number' && error.status >= 500) return true;
    return false;
  }

  async function request(url, options) {
    const opts = options || {};
    const timeoutMs = Number.isFinite(opts.timeoutMs) ? opts.timeoutMs : 10000;
    const maxRetries = Number.isFinite(opts.retry) ? opts.retry : 0;
    const responseType = opts.responseType || 'json';
    const retryDelayMs = Number.isFinite(opts.retryDelayMs) ? opts.retryDelayMs : 250;

    let attempt = 0;
    let lastError = null;

    while (attempt <= maxRetries) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort('timeout'), timeoutMs);
      const externalSignal = opts.signal;

      const onExternalAbort = function() {
        controller.abort('external');
      };

      if (externalSignal) {
        if (externalSignal.aborted) {
          clearTimeout(timer);
          throw createError('aborted', 'Request aborted');
        }
        externalSignal.addEventListener('abort', onExternalAbort, { once: true });
      }

      try {
        const response = await fetch(url, {
          method: opts.method || 'GET',
          headers: opts.headers || {},
          body: opts.body,
          signal: controller.signal
        });

        if (!response.ok) {
          throw createError('http', `HTTP error ${response.status}`, {
            status: response.status,
            response: response
          });
        }

        if (responseType === 'text') {
          return response.text();
        }
        if (responseType === 'json') {
          return response.json();
        }
        return response;
      } catch (error) {
        if (error && error.kind) {
          lastError = error;
        } else if (error && error.name === 'AbortError') {
          if (externalSignal && externalSignal.aborted) {
            lastError = createError('aborted', 'Request aborted', { originalError: error });
          } else {
            lastError = createError('timeout', 'Request timed out', { originalError: error });
          }
        } else if (error instanceof TypeError) {
          lastError = createError('network', 'Network error', { originalError: error });
        } else {
          lastError = createError('unknown', 'Request failed', { originalError: error });
        }
      } finally {
        clearTimeout(timer);
        if (externalSignal) {
          externalSignal.removeEventListener('abort', onExternalAbort);
        }
      }

      if (lastError && !isRetryable(lastError)) {
        throw lastError;
      }

      if (attempt >= maxRetries) {
        throw lastError || createError('unknown', 'Request failed');
      }

      await wait(retryDelayMs * (attempt + 1));
      attempt += 1;
    }

    throw lastError || createError('unknown', 'Request failed');
  }

  window.themeRequest = {
    fetchJSON: function(url, options) {
      return request(url, Object.assign({}, options, { responseType: 'json' }));
    },
    fetchText: function(url, options) {
      return request(url, Object.assign({}, options, { responseType: 'text' }));
    },
    isRetryableError: isRetryable
  };
})();
